import { Fragment, useCallback, useState } from 'react';
import { Box, Grid, Group, Paper, SegmentedControl, Stack, Table, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type {
  EnvironmentalDataFormData,
  KilnLoadMassFlowCoverage,
  RotaryKilnLoadResult,
} from '../../../types/reports.js';
import { DECIMAL_INPUT_PROPS, EMISSION_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { MATRIX_TABLE_STYLES, MATRIX_TOTAL_TH, MATRIX_VALUE_TH } from '../../form/matrixPresets.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { LabelWithInfo } from '../../ui/LabelWithInfo.js';
import {
  CALCULATION_BASIS_CONCENTRATION,
  CALCULATION_BASIS_MASS_FLOW,
  KILN_LOAD_CONTINUOUS_GROUPS,
  KILN_LOAD_DISCONTINUOUS_GROUPS,
  KILN_LOAD_OPERATING_ROWS,
  massFlowCompoundKey,
  massFlowDirectKey,
  type KilnLoadCalculationBasis,
} from './kilnFields.js';
import {
  formatFlowValue,
  formatHoursValue,
  formatLoadValue,
  formatOriginDate,
  hasWeightedFlow,
  missingStateSentence,
  missingStateTooltip,
} from './kilnLoadFormat.js';

interface KilnLoadCalculationFormProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  kilnIdx: number;
  result: RotaryKilnLoadResult | null;
  disabled?: boolean;
}

// Kontinuierliche Komponenten (D-20, Sketch 024 `contBlock`): sieben Felder, auf zwei Tabellen
// verteilt, ceil(7/2) = 4 links -- KEIN Modusparameter, diese Gruppe rechnet immer ueber die
// Konzentration (RESEARCH Pitfall 7).
const CONTINUOUS_FIELDS = KILN_LOAD_CONTINUOUS_GROUPS[0]?.fields ?? [];
const CONTINUOUS_SPLIT = Math.ceil(CONTINUOUS_FIELDS.length / 2);
const CONTINUOUS_LEFT_FIELDS = CONTINUOUS_FIELDS.slice(0, CONTINUOUS_SPLIT);
const CONTINUOUS_RIGHT_FIELDS = CONTINUOUS_FIELDS.slice(CONTINUOUS_SPLIT);

// Diskontinuierliche Gruppen (D-21): Schwermetalle rechts als geschlossener Block, der Rest
// links -- dieselbe Aufteilung wie im Vorgaenger (Plan 38.1-01), jetzt nur ueber die drei
// diskontinuierlichen Gruppen statt ueber alle vier. `massFlowUnit` steht an jedem Feld dieser
// Gruppen (kilnFields.ts, Phase 40); die Vereinigung von KILN_LOAD_COMPONENT_GROUPS traegt diese
// Eigenschaft typseitig nicht an jedem Zweig (Union aus kontinuierlicher/diskontinuierlicher
// Feldform, siehe `kilnFields.test.ts`), deshalb hier einmalig als die schmalere, tatsaechlich
// zutreffende Form behandelt statt an jeder Zugriffsstelle erneut zu casten.
interface DiscontinuousLoadField {
  key: string;
  label: string;
  unit: string;
  massFlowUnit: string;
}
interface DiscontinuousLoadGroup {
  key: string;
  title: string;
  fields: DiscontinuousLoadField[];
}
const DISCONTINUOUS_GROUPS = KILN_LOAD_DISCONTINUOUS_GROUPS as DiscontinuousLoadGroup[];
const DISCONTINUOUS_GROUPS_LEFT = DISCONTINUOUS_GROUPS.filter((group) => group.key !== 'heavyMetals');
const DISCONTINUOUS_GROUPS_RIGHT = DISCONTINUOUS_GROUPS.filter((group) => group.key === 'heavyMetals');

// Die beiden diskontinuierlichen Tabellen stehen nur nebeneinander, wenn beide in ihre halbe
// Breite passen -- gemessen an der Breite des Grids selbst (Container Query), nicht am Fenster:
// die Seitenleiste nimmt dem Inhalt rund 400px. Schwellen aus der Mindestbreite der Tabellen im
// Browser (`width: min-content`, Karlstadt Drehofen 1): Massenstrom 553px (Ø VB + Ø DB, mit
// Info-Icon an der Komponente), Konzentration 383px; je zwei Tabellen plus 32px Abstand (`gap="xl"`).
// Darunter volle Breite, Schwermetalle dann unter dem Rest. Nur `md` wird benutzt, die uebrigen
// Stufen muessen fuer den Typ gesetzt sein.
function sideBySideFrom(minGridWidth: number) {
  return { xs: '1px', sm: '2px', md: `${minGridWidth}px`, lg: '100000px', xl: '100001px' };
}
const DISCONTINUOUS_GRID_BREAKPOINTS = {
  concentration: sideBySideFrom(800),
  massFlow: sideBySideFrom(1150),
};

/** Frachtzelle (letzte Spalte beider Tabellenarten): ruhiger Text, graue Einheit, '—' ohne Eintrag. */
function LoadCell({ loadEntry }: { loadEntry?: { value: string; unit: string } }) {
  if (!loadEntry) {
    return (
      <Text ta="right" size="sm" c="vdzGray.6">
        —
      </Text>
    );
  }
  return (
    <Text ta="right" size="sm">
      {formatLoadValue(loadEntry.value)}{' '}
      <Text component="span" c="vdzGray.6">
        {loadEntry.unit}
      </Text>
    </Text>
  );
}

interface ContinuousLoadTableProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  kilnIdx: number;
  result: RotaryKilnLoadResult | null;
  disabled?: boolean;
  fields: typeof CONTINUOUS_FIELDS;
}

/**
 * Kontinuierlich gemessene Komponenten (D-20): feste 3-Spalten-Tabelle, immer Konzentration,
 * keine Gruppen-Zwischenzeile (Sketch `contBlock` listet die Felder ohne eigenen Zwischentitel --
 * der Blocktitel "Kontinuierlich gemessen" traegt das bereits).
 */
function ContinuousLoadTable({ form, kilnIdx, result, disabled, fields }: ContinuousLoadTableProps) {
  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table striped="odd" styles={MATRIX_TABLE_STYLES} verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Komponente</Table.Th>
            <Table.Th style={MATRIX_VALUE_TH}>Konzentration, nicht validiert</Table.Th>
            <Table.Th style={MATRIX_TOTAL_TH}>Fracht</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {fields.map((field) => (
            <Table.Tr key={field.key}>
              <Table.Td>
                <Text size="sm">{field.label}</Text>
              </Table.Td>
              <Table.Td>
                <ValidatedNumberInput
                  form={form}
                  field={`rotaryKilns.${kilnIdx}.loadCalculation.${field.key}Value`}
                  kind="decimal"
                  align="right"
                  unit={field.unit}
                  aria-label={`${field.label} Konzentration`}
                  disabled={disabled}
                  {...EMISSION_INPUT_PROPS}
                />
              </Table.Td>
              <Table.Td>
                <LoadCell loadEntry={result?.loads?.[field.key]} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

interface LoadGroupTableProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  kilnIdx: number;
  result: RotaryKilnLoadResult | null;
  disabled?: boolean;
  groups: DiscontinuousLoadGroup[];
  mode: KilnLoadCalculationBasis;
  coverage: KilnLoadMassFlowCoverage | null;
}

/**
 * Eine der beiden Frachtenspalten diskontinuierlich gemessener Komponenten (D-21, Aufbau aus
 * Plan 38.1-01 unveraendert uebernommen): je Gruppe eine Zwischenzeile, je Komponente eine Zeile.
 * Im Konzentrations-Modus wie bisher ein Eingabefeld auf `<key>Value`; im Massenstrom-Modus zwei
 * Eingabefelder auf `<key>MassFlowCompound`/`<key>MassFlowDirect` (D-06) mit `massFlowUnit`.
 * Der Einzelhinweis (D-23) kommt AUSSCHLIESSLICH aus `coverage.components` (Serverergebnis) --
 * kein Ruecklesen der Oe-Formularwerte, damit der angezeigte Hinweis nie von der tatsaechlich
 * gerechneten Fracht abweichen kann (T-40-22). Der Satz-Fall (D-22, `hasSentence`) hat Vorrang:
 * ist er aktiv, traegt keine Zeile ein Info-Icon (der Server leert `components` in diesem Fall
 * ohnehin, diese Pruefung ist die zusaetzliche Frontend-Absicherung).
 */
function LoadGroupTable({ form, kilnIdx, result, disabled, groups, mode, coverage }: LoadGroupTableProps) {
  const isMassFlow = mode === CALCULATION_BASIS_MASS_FLOW;
  const hasSentence = Boolean(coverage?.missingState);
  const componentHints =
    !hasSentence && coverage?.components && !Array.isArray(coverage.components) ? coverage.components : null;
  const colSpan = isMassFlow ? 4 : 3;

  return (
    <Box style={{ overflowX: 'auto' }}>
      <Table striped="odd" styles={MATRIX_TABLE_STYLES} verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Komponente</Table.Th>
            {isMassFlow ? (
              <>
                <Table.Th style={MATRIX_VALUE_TH}>Ø Verbundbetrieb</Table.Th>
                <Table.Th style={MATRIX_VALUE_TH}>Ø Direktbetrieb</Table.Th>
              </>
            ) : (
              <Table.Th style={MATRIX_VALUE_TH}>Konzentration, nicht validiert</Table.Th>
            )}
            <Table.Th style={MATRIX_TOTAL_TH}>Fracht</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              <Table.Tr bg="white">
                <Table.Th colSpan={colSpan}>{group.title}</Table.Th>
              </Table.Tr>
              {group.fields.map((field) => {
                const hint = componentHints?.[field.key];
                return (
                  <Table.Tr key={field.key}>
                    <Table.Td>
                      {hint ? (
                        <LabelWithInfo
                          label={
                            <Text component="span" size="sm">
                              {field.label}
                            </Text>
                          }
                          info={missingStateTooltip(hint.missingState, hint.coveredHours)}
                        />
                      ) : (
                        <Text size="sm">{field.label}</Text>
                      )}
                    </Table.Td>
                    {isMassFlow ? (
                      <>
                        <Table.Td>
                          <ValidatedNumberInput
                            form={form}
                            field={`rotaryKilns.${kilnIdx}.loadCalculation.${massFlowCompoundKey(field.key)}`}
                            kind="decimal"
                            align="right"
                            unit={field.massFlowUnit}
                            aria-label={`${field.label} Verbundbetrieb`}
                            disabled={disabled}
                            {...EMISSION_INPUT_PROPS}
                          />
                        </Table.Td>
                        <Table.Td>
                          <ValidatedNumberInput
                            form={form}
                            field={`rotaryKilns.${kilnIdx}.loadCalculation.${massFlowDirectKey(field.key)}`}
                            kind="decimal"
                            align="right"
                            unit={field.massFlowUnit}
                            aria-label={`${field.label} Direktbetrieb`}
                            disabled={disabled}
                            {...EMISSION_INPUT_PROPS}
                          />
                        </Table.Td>
                      </>
                    ) : (
                      <Table.Td>
                        <ValidatedNumberInput
                          form={form}
                          field={`rotaryKilns.${kilnIdx}.loadCalculation.${field.key}Value`}
                          kind="decimal"
                          align="right"
                          unit={field.unit}
                          aria-label={`${field.label} Konzentration`}
                          disabled={disabled}
                          {...EMISSION_INPUT_PROPS}
                        />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <LoadCell loadEntry={result?.loads?.[field.key]} />
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Fragment>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
}

/**
 * Das Rechenblatt eines einzelnen Ofens (Sketch 024 Variante D, Plan 40-07): Betriebsdaten
 * unveraendert (D-07/D-14), darunter zwei eigene Bloecke -- "Kontinuierlich gemessen" (immer
 * Konzentration, D-20) und "Diskontinuierlich gemessen" (Auswahl Konzentration/Massenstrom aus
 * Emissionsbericht, D-21). Ersetzt den bisherigen gemeinsamen Unterabschnitt "Konzentrationen und
 * Frachten" (Sketch 022 D, Plan 38.1-01/40-06).
 *
 * Die Rechengrundlage (`calculationBasis`) haengt am uncontrolled-Formular
 * (`form.getInputProps`/`form.key`, keine eigene onChange-Logik) und wird zusaetzlich per
 * `form.watch` in lokalem State gespiegelt (Muster `KilnLoadMeta` in `KilnLoadSection.tsx`) --
 * ein uncontrolled-Formular rendert sonst nicht neu, wenn sich nur `calculationBasis` aendert.
 * Beim Umschalten werden keine Formularwerte geloescht (D-09): Konzentrationen bleiben im
 * Formularstand stehen, wenn auf Massenstrom gewechselt wird, und umgekehrt.
 *
 * `weightedFlow`/`totalOperatingHours`/`loads`/`massFlowCoverage` kommen AUSSCHLIESSLICH aus
 * `result` (D-19/D-22/D-23) -- nie im Browser nachgerechnet. `massFlowOrigin` (D-24) kommt aus
 * dem Formularstand (report:read, hydriert wie die uebrigen Rechenblatt-Felder).
 */
export function KilnLoadCalculationForm({
  form,
  kilnIdx,
  result,
  disabled,
}: KilnLoadCalculationFormProps) {
  const filled = hasWeightedFlow(result);
  const basisFieldPath = `rotaryKilns.${kilnIdx}.loadCalculation.calculationBasis`;

  const [basis, setBasis] = useState<KilnLoadCalculationBasis>(() => {
    const stored = form.getValues().rotaryKilns?.[kilnIdx]?.loadCalculation?.calculationBasis;
    return stored === CALCULATION_BASIS_MASS_FLOW ? CALCULATION_BASIS_MASS_FLOW : CALCULATION_BASIS_CONCENTRATION;
  });

  const handleBasisChange = useCallback(({ value }: { value: unknown }) => {
    setBasis(value === CALCULATION_BASIS_MASS_FLOW ? CALCULATION_BASIS_MASS_FLOW : CALCULATION_BASIS_CONCENTRATION);
  }, []);

  form.watch(basisFieldPath, handleBasisChange);

  const isMassFlow = basis === CALCULATION_BASIS_MASS_FLOW;
  const coverage = result?.massFlowCoverage ?? null;
  const massFlowOrigin = form.getValues().rotaryKilns?.[kilnIdx]?.loadCalculation?.massFlowOrigin ?? null;

  return (
    <Stack gap="lg">
      <FormSubsection title="Betriebsdaten">
        <Stack gap="md">
          <Stack gap="xs">
            <Text size="sm" c="vdzGray.9" fw={700}>
              Verbundbetrieb
            </Text>
            <Grid>
              {KILN_LOAD_OPERATING_ROWS.map((row) => (
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={row.compoundKey}>
                  <ValidatedNumberInput
                    form={form}
                    field={`rotaryKilns.${kilnIdx}.loadCalculation.${row.compoundKey}`}
                    kind="decimal"
                    align="right"
                    label={row.label}
                    unit={row.unit}
                    aria-label={`${row.label} Verbundbetrieb`}
                    disabled={disabled}
                    {...DECIMAL_INPUT_PROPS}
                    decimalScale={3}
                    step={0.001}
                  />
                </Grid.Col>
              ))}
            </Grid>
          </Stack>

          <Stack gap="xs">
            <Group gap={6}>
              <Text size="sm" c="vdzGray.9" fw={700}>
                Direktbetrieb
              </Text>
              <Text component="span" size="xs" fw={400} c="vdzGray.6">
                optional
              </Text>
            </Group>
            <Grid>
              {KILN_LOAD_OPERATING_ROWS.map((row) => (
                <Grid.Col span={{ base: 12, md: 6, lg: 4 }} key={row.directKey}>
                  <ValidatedNumberInput
                    form={form}
                    field={`rotaryKilns.${kilnIdx}.loadCalculation.${row.directKey}`}
                    kind="decimal"
                    align="right"
                    label={row.label}
                    unit={row.unit}
                    aria-label={`${row.label} Direktbetrieb`}
                    disabled={disabled}
                    {...DECIMAL_INPUT_PROPS}
                    decimalScale={3}
                    step={0.001}
                  />
                </Grid.Col>
              ))}
            </Grid>
          </Stack>

          {/* Zwischenergebnis -- der Angelpunkt: wer von seiner eigenen Rechnung abweicht, weicht
              meistens hier ab. Gefuellt in der Summenfarbe des Portals (vdzYellow, wie
              `KilnCountBadge` in `EnvironmentalDataForm.tsx`), leer im Theme-Standard (weiss,
              Rand vdzGray-2 -- Paper-Default aus theme.ts). Werte ausschliesslich aus `result`,
              nie aus dem Formularstand berechnet. */}
          <Paper
            bg={filled ? 'vdzYellow.1' : undefined}
            style={filled ? { borderColor: 'var(--mantine-color-vdzYellow-4)' } : undefined}
            radius="md"
            p="md"
          >
            <Group justify="space-between" align="center" wrap="nowrap" gap="md">
              <Stack gap={2}>
                <Text size="xs" fw={700} c={filled ? 'vdzYellow.8' : 'vdzGray.7'}>
                  Gewichteter Volumenstrom bei 10 Vol.-% O₂
                </Text>
                <Text size="xs" c="vdzGray.6">
                  {filled
                    ? `Gesamtlaufzeit ${formatHoursValue(result?.totalOperatingHours)} h`
                    : 'Volumenstrom, O₂-Gehalt und Betriebsstunden im Verbundbetrieb eintragen, dann rechnen sich die Frachten.'}
                </Text>
              </Stack>
              <Text size="xl" c={filled ? 'vdzGray.9' : 'vdzGray.6'} fw={700} style={{ whiteSpace: 'nowrap' }}>
                {filled ? (
                  <>
                    {formatFlowValue(result?.weightedFlow)}{' '}
                    <Text component="span" size="sm" fw={400} c="vdzGray.6">
                      m³/h
                    </Text>
                  </>
                ) : (
                  '—'
                )}
              </Text>
            </Group>
          </Paper>
        </Stack>
      </FormSubsection>

      <FormSubsection
        title="Kontinuierlich gemessen"
        description="Nicht validierte Jahresmittelwerte eintragen. Die Fracht ergibt sich aus Konzentration, gewichtetem Volumenstrom und Gesamtlaufzeit."
      >
        <Paper radius="md" p="md">
          <Grid gap="xl" align="flex-start">
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <ContinuousLoadTable
                form={form}
                kilnIdx={kilnIdx}
                result={result}
                disabled={disabled}
                fields={CONTINUOUS_LEFT_FIELDS}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <ContinuousLoadTable
                form={form}
                kilnIdx={kilnIdx}
                result={result}
                disabled={disabled}
                fields={CONTINUOUS_RIGHT_FIELDS}
              />
            </Grid.Col>
          </Grid>
        </Paper>
      </FormSubsection>

      <FormSubsection
        title="Diskontinuierlich gemessen"
        rightSection={
          <SegmentedControl
            data={[
              { value: CALCULATION_BASIS_CONCENTRATION, label: 'Konzentration' },
              { value: CALCULATION_BASIS_MASS_FLOW, label: 'Massenstrom aus Emissionsbericht' },
            ]}
            aria-label="Rechengrundlage diskontinuierlich gemessener Komponenten"
            disabled={disabled}
            key={form.key(basisFieldPath)}
            {...form.getInputProps(basisFieldPath)}
            // `form.getInputProps` liefert im uncontrolled Modus den ROHEN, nicht normalisierten
            // Formularwert als `defaultValue` -- `null` (D-09-Bedeutung "Konzentration") trifft
            // keinen der beiden `data`-Werte, kein Radio waere markiert. `basis` (Zustand oben,
            // beim Mount bereits normalisiert) ersetzt den Default deshalb explizit; die
            // Steuerung selbst bleibt unveraendert unkontrolliert (onChange kommt weiterhin aus
            // `getInputProps`).
            defaultValue={basis}
          />
        }
      >
        <Stack gap="sm">
          {isMassFlow ? (
            massFlowOrigin ? (
              <Text size="sm" c="vdzGray.6">
                Vorbelegt aus{' '}
                <Text component="span" inherit fw={700} c="vdzGray.9">
                  {massFlowOrigin.filename}
                </Text>
                , übernommen am {formatOriginDate(massFlowOrigin.acceptedAt)}. Mittel der Messungen je
                Betriebszustand, Werte unter der Bestimmungsgrenze mit halbem Wert. Änderungen sind möglich.
              </Text>
            ) : (
              <Text size="sm" c="vdzGray.6">
                Noch kein Emissionsbericht übernommen. Die Mittelwerte werden beim Übernehmen eines
                Berichts vorbelegt und lassen sich auch selbst eintragen.
              </Text>
            )
          ) : (
            <Text size="sm" c="vdzGray.6">
              Konzentrationen der Einzelmessungen als Mittelwert in der angegebenen Einheit eintragen,
              bezogen auf 10 Vol.-% O₂.
            </Text>
          )}

          <Paper radius="md" p="md">
            {isMassFlow && (
              <Text size="sm" c="vdzGray.7" mb={coverage?.missingState ? 'xs' : 'md'}>
                Fracht = (Ø Verbundbetrieb ×{' '}
                <Text component="span" inherit fw={700} c="vdzGray.9">
                  {formatHoursValue(coverage?.compoundHours)} h
                </Text>{' '}
                + Ø Direktbetrieb ×{' '}
                <Text component="span" inherit fw={700} c="vdzGray.9">
                  {formatHoursValue(coverage?.directHours)} h
                </Text>
                ) / 1.000
              </Text>
            )}
            {isMassFlow && coverage?.missingState && (
              <Text size="sm" c="vdzGray.8" mb="md">
                {missingStateSentence(coverage.missingState, coverage.coveredHours)}
              </Text>
            )}
            <Grid
              type="container"
              breakpoints={isMassFlow ? DISCONTINUOUS_GRID_BREAKPOINTS.massFlow : DISCONTINUOUS_GRID_BREAKPOINTS.concentration}
              gap="xl"
              align="flex-start"
            >
              <Grid.Col span={{ base: 12, md: 6 }}>
                <LoadGroupTable
                  form={form}
                  kilnIdx={kilnIdx}
                  result={result}
                  disabled={disabled}
                  groups={DISCONTINUOUS_GROUPS_LEFT}
                  mode={basis}
                  coverage={coverage}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <LoadGroupTable
                  form={form}
                  kilnIdx={kilnIdx}
                  result={result}
                  disabled={disabled}
                  groups={DISCONTINUOUS_GROUPS_RIGHT}
                  mode={basis}
                  coverage={coverage}
                />
              </Grid.Col>
            </Grid>
          </Paper>
        </Stack>
      </FormSubsection>
    </Stack>
  );
}
